import * as loadJsCss from "load-js-css";
import Renderer from "../Renderer";
import Utils from "../utils/Utils";

/**
 * @creator Stefano
 * @date 1-4-2018
 */
export default class AdvancedController {
    constructor(latestVersions) {
        this.latestVersions = latestVersions;
        this.spreads = new Map();
        this._versions = new Map();
        this._scene_id = false;
        this.ACVersion = {};
    }

    resetSpreads() {
        this.spreads = new Map();
        this._versions = new Map();
    }

    getLatestVersion(major) {
        return (this.latestVersions && this.latestVersions[major]) || major;
    }

    /**
     * @return {Promise<any>}
     */
    setAdvancedBundleInstance(version) {
        if (!this._isLoading) {
            this._isLoading = new Promise((resolve) => {
                let prefix_url;

                if (Utils.isDownloaded() || !self.system_domain) {
                    // inline downloaded version
                    prefix_url = "";
                } else {
                    //normal version
                    prefix_url = `${self.system_domain}/advanced/`;
                }

                loadJsCss.list([
                    {
                        path: `${prefix_url}css/advanced_v${version}.css?v=${this.getLatestVersion(version)}`,
                        type: "css",
                    },
                    {
                        path: `${prefix_url}js/advanced_v${version}.js?v=${this.getLatestVersion(version)}`,
                        type: "js",
                        callback: () => resolve(),
                    },
                ]);
            });
        }
        return this._isLoading;
    }

    /**
     * Given controller should be given a version to be loaded of a debug version
     * @return {Promise<any>}
     */
    getController(version = 10) {
        if (Utils.isEmpty(version) || version === 0) {
            version = 10;
        }
        return new Promise((resolve, reject) => {
            if (Utils.isEmpty(this.ACVersion[version])) {
                this.setAdvancedBundleInstance(version)
                    .then(() => {
                        this._isLoading = null;
                        // Renderer.bindKeyDownEvents();
                        let major = version; //fallback for if getBuildInfo fails
                        try {
                            major = window.maglrController.getBuildInfo().major;
                        } catch (error) {
                            console.error("MAGLR ERROR: [getController]", error);
                        }
                        this.ACVersion[major] = window.maglrController;
                        resolve(this.ACVersion[major]);
                    })
                    .catch((e) => {
                        reject(e);
                    });
            } else {
                resolve(this.ACVersion[version]);
            }
        });
    }

    createSpreadContainer(spread_id, assetsVersion) {
        const container = document.getElementById(`${spread_id}`);
        if (container && container.children.length === 0) {
            Renderer.nativeInject(
                spread_id,
                `<article tabindex="-1" id="spread_${spread_id}" data-src="inline" class="spread spread_v${assetsVersion}" data-spread_id="${spread_id}"></article>`,
            );
            return container.children[0];
        }
    }

    /**
     *
     * @param spread_id
     * @param data
     * @param version
     * @param spreadOptions
     */
    createSpread(spread_id, data, version, spreadOptions) {
        let created = false;
        try {
            this._versions.set(spread_id, version);
            if (data !== false) {
                if (!this.spreads.has(spread_id)) {
                    let options = {};

                    if (this._scene_id) {
                        options.scene = this._scene_id;
                        this._scene_id = false;
                    }

                    if (self.replaceables && self.replaceables[spread_id] != undefined) {
                        options.replaceables = self.replaceables[spread_id];
                        if (self.replaceables.global && !self.replaceables.global[this._versions.get(spread_id)]) {
                            self.replaceables.global[this._versions.get(spread_id)] = true;
                        }
                    }

                    this.getController(this._versions.get(spread_id)).then((advanced) => {
                        if (advanced.getBuildInfo().major > 9) {
                            if (spreadOptions) {
                                spreadOptions.interfaceOffset &&
                                    advanced.setInterfaceSettings(spreadOptions.interfaceOffset);

                                if (spreadOptions.spreadOffset) {
                                    options.spreadOffset = spreadOptions.spreadOffset;
                                }
                            }

                            options.scrollingBody = true;

                            const spreadElement =
                                document.getElementById(`spread_${spread_id}`) ??
                                this.createSpreadContainer(spread_id, version);
                            data.settings?._spread_globals?.spread_title &&
                                spreadElement &&
                                spreadElement.setAttribute("aria-label", data.settings._spread_globals.spread_title);
                        }
                        if (self.replaceables && self.replaceables.global) {
                            advanced.setGlobalData(self.replaceables.global);
                        }
                        advanced.createSpread(spread_id, data, options);
                    });
                    created = true;
                } else {
                    if (Utils.isDownloaded()) {
                        this.showSpread(spread_id);
                    }
                }
            }
            this.spreads.set(spread_id, data);

            return created;
        } catch (error) {
            console.error("MAGLR ERROR: [createSpread]", error);
        }
    }

    getElement(spread_id) {
        return $("#spread_" + spread_id);
    }

    showSpread(spread_id) {
        if (this.spreads.has(spread_id)) {
            this.getController(this._versions.get(spread_id)).then((advanced) => {
                advanced.showSpread(spread_id);
            });
            return true;
        }
    }

    startSpread(spread_id, offset) {
        if (this.spreads.has(spread_id)) {
            if ($("#spread_" + spread_id).hasClass("shown")) {
                let settings = {
                    swipeDisabled: true,
                };
                if (this._scene_id) {
                    settings.scene = this._scene_id;
                    this._scene_id = false;
                }

                if (offset) {
                    settings.offset = offset;
                }

                if (this.multiActions?.spreadId === spread_id) {
                    settings.multiActions = this.multiActions.actions;
                    delete this.multiActions;
                }

                this.getController(this._versions.get(spread_id)).then((advanced) => {
                    advanced.startSpread(spread_id, settings);
                });
            }
        }
    }

    pauseSpread(spread_id) {
        if (this.spreads.has(spread_id)) {
            try {
                this.getController(this._versions.get(spread_id)).then((advanced) => {
                    advanced?.pauseSpread?.(spread_id);
                });
            } catch (error) {
                console.error("MAGLR ERROR: [pauseSpread]", error);
            }
        }
    }

    stopSpread(spread_id) {
        if (this.spreads.has(spread_id)) {
            this.getController(this._versions.get(spread_id)).then((advanced) => {
                advanced.stopSpread(spread_id);
            });
        }
        return this;
    }

    destroySpread(spread_id) {
        try {
            if (this.spreads.has(spread_id)) {
                this.spreads.delete(spread_id);
                $("#spread_" + spread_id).empty();
                document
                    .getElementById(`maglr-article-${spread_id}`)
                    .querySelector(".spreadloading")
                    ?.classList.remove("loaded");
                return this.getController(this._versions.get(spread_id)).then((advanced) => {
                    advanced.destroySpread(spread_id);
                });
            }
        } catch (error) {
            console.error("MAGLR ERROR: [destroySpread]", error);
        }
    }

    /**
     * Prevent any instances from floating around in cyberspace.
     */
    destroyAll() {
        this.spreads.forEach((entry) => {
            this.destroySpread(entry.spread_id);
        });
    }

    updateInterfaceOffset(offset) {
        for (let version in this.ACVersion) {
            if (version > 10) {
                this.ACVersion[version].setInterfaceSettings(offset);
            }
        }
    }

    setCookieConsented(consented) {
        //TODO dit nog verder aanlsuiten
        for (let version in this.ACVersion) {
            if (version > 10) {
                this.ACVersion[version].setCookieConsented(consented);
            }
        }
    }

    sceneLink(spread_id, direction) {
        if (this.spreads.has(spread_id)) {
            try {
                this.getController(this._versions.get(spread_id)).then((advanced) => {
                    if (direction === "NEXT") {
                        advanced?.nextScene();
                    } else {
                        advanced?.previousScene();
                    }
                });
            } catch (error) {
                console.error("MAGLR ERROR: [sceneLink]", error);
            }
        }
    }

    reportScrollDepth(spreadId) {
        if (this.spreads.has(spreadId)) {
            try {
                this.getController(this._versions.get(spreadId)).then((advanced) => {
                    advanced?.reportScrollDepth?.();
                });
            } catch (error) {
                console.error("MAGLR ERROR: [reportScrollDepth]", error);
            }
        }
    }

    setMultiActionLink(spreadId, actions) {
        this.multiActions = { spreadId, actions };
    }

    executeMultiActionLink(spreadId, actions) {
        if (this.spreads.has(spreadId)) {
            try {
                this.getController(this._versions.get(spreadId)).then((advanced) => {
                    advanced.multiActionLink?.(actions); // multiActionLink function only available in v12 and up
                });
            } catch (error) {
                console.error("MAGLR ERROR: [executeMultiActionLink]", error);
            }
        }
    }
}
